import React, { Activity, createContext, useContext, useEffect, useMemo, useState } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { avatarDefault, profileBackground } from '@/assets/images';
import { AppHeader } from '@/components/layout/app-header';
import { Stack } from '@/components/layout/stack';
import { Image, Level, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { PlayerProfileEquipment } from '@/features/player/components/player-profile/player-equipment/player-profile-equipment';
import { PlayerProfileNotesContent } from '@/features/player/components/player-profile/player-note/player-profile-notes-content';
import { PlayerProfileOverviewContent } from '@/features/player/components/player-profile/player-overview/player-profile-overview-content';
import { PlayerProfileProgressTrackerContent } from '@/features/player/components/player-profile/player-progress-tracker/player-profile-progress-tracker-content';
import type { PlayerProfileData } from '@/features/player/components/types';
import { DEFAULT_PROFILE } from '@/features/player/hooks/use-player-profile';
import { createStyles, useScaling } from '@/hooks';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { AsYouType } from 'libphonenumber-js';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type ProfileTabKey = 'overview' | 'progressTracker' | 'notes' | 'equipment';

type PlayerProfileContextValue = {
  activeTab: ProfileTabKey;
  setActiveTab: React.Dispatch<React.SetStateAction<ProfileTabKey>>;
  tabs: { key: ProfileTabKey; label: string }[];
  profile: PlayerProfileData;
  saveNote?: (payload: { title: string; detail: string }) => Promise<void>;
  mutateDeleteReturnTo?: 'SelectPlayer' | 'OverviewSession';
};

const PlayerProfileContext = createContext<PlayerProfileContextValue | null>(null);

function usePlayerProfileContext() {
  const context = useContext(PlayerProfileContext);
  if (context) return context;

  return {
    activeTab: 'overview' as ProfileTabKey,
    setActiveTab: (() => {
      return;
    }) as React.Dispatch<React.SetStateAction<ProfileTabKey>>,
    tabs: [
      { key: 'overview' as ProfileTabKey, label: STRINGS.player.profile.tabs.overview },
      {
        key: 'progressTracker' as ProfileTabKey,
        label: STRINGS.player.profile.tabs.progressTracker,
      },
      { key: 'notes' as ProfileTabKey, label: STRINGS.player.profile.tabs.notes },
      { key: 'equipment' as ProfileTabKey, label: STRINGS.player.profile.tabs.equipment },
    ],
    mutateDeleteReturnTo: undefined,
    profile: {
      id: 'fallback',
      name: '-',
      ageText: '',
      level: 1,
      lastAttended: undefined,
      parentName: '-',
      phone: '-',
      email: '-',
      notes: [],
      progress: [],
      welcomeKit: [],
      clubsneeded: false,
      dominantHand: '',
      height: '',
    },
  } as PlayerProfileContextValue;
}

type PlayerProfileRootProps = {
  children: React.ReactNode;
  profile: PlayerProfileData;
  saveNote?: (payload: { title: string; detail: string }) => Promise<void>;
  mutateDeleteReturnTo?: 'SelectPlayer' | 'OverviewSession';
};

function PlayerProfileRoot({
  children,
  profile,
  saveNote,
  mutateDeleteReturnTo,
}: PlayerProfileRootProps) {
  const { isTablet } = useScaling();
  const [activeTab, setActiveTab] = useState<ProfileTabKey>(
    isTablet ? 'progressTracker' : 'overview',
  );

  const tabs = useMemo(
    () => [
      { key: 'overview' as const, label: STRINGS.player.profile.tabs.overview },
      { key: 'progressTracker' as const, label: STRINGS.player.profile.tabs.progressTracker },
      { key: 'notes' as const, label: STRINGS.player.profile.tabs.notes },
      { key: 'equipment' as const, label: STRINGS.player.profile.tabs.equipment },
    ],
    [],
  );

  useEffect(() => {
    if (isTablet && activeTab === 'overview') {
      setActiveTab('progressTracker');
    }
  }, [activeTab, isTablet]);

  return (
    <PlayerProfileContext.Provider
      value={{ activeTab, setActiveTab, tabs, profile, saveNote, mutateDeleteReturnTo }}>
      <Stack flex={1}>{children}</Stack>
    </PlayerProfileContext.Provider>
  );
}

function PlayerProfileTop() {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const { profile } = usePlayerProfileContext();

  const avatarSize = scale(100);

  return (
    <Stack backgroundColor="white">
      <Image source={profileBackground} width={'100%'} height={scale(132)} resizeMode="cover" />

      <Stack style={styles.avatarWrap}>
        <Image
          source={profile.avatarUri ? { uri: profile.avatarUri } : avatarDefault}
          width={avatarSize}
          height={avatarSize}
          circular
          defaultSource={avatarDefault}
          resizeMode="cover"
        />
      </Stack>

      <Stack align="center" style={styles.profileBlock} zIndex={999} backgroundColor="white">
        <Text
          fontWeight={'800'}
          variant="Font Montserrat/Body/Body 2 Bold 16px"
          style={styles.playerName}>
          {profile.name}
        </Text>
        <Stack direction="row" align="center" gap={scale(12)} style={styles.metaRow}>
          <Level style={styles.levelPill} value={profile.level} alignSelf="flex-start" />
          <Activity mode={profile.ageText ? 'visible' : 'hidden'}>
            <Text
              variant={
                isTablet ? 'Font Inter/Subtitle/Regular 12px' : 'Font Inter/Subtitle/Regular'
              }
              style={styles.ageText}>
              {profile.ageText}
            </Text>
          </Activity>
        </Stack>
      </Stack>
    </Stack>
  );
}

function PlayerProfileHeader() {
  const styles = useStyles();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { scale, isTablet } = useScaling();
  const { profile, mutateDeleteReturnTo } = usePlayerProfileContext();

  return (
    <AppHeader
      paddingTop={insets.top + scale({ phone: 12, tablet: 21 })}
      paddingBottom={scale({ phone: 15, tablet: 21 })}
      backgroundColor="white">
      <AppHeader.Left>
        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => {
            navigation.goBack();
          }}>
          <Stack direction="row" align="center" gap={scale(2)}>
            <SvgIcon name="IcBackBlue" size={24} />
            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'}
              style={styles.headerActionText}>
              {STRINGS.player.profile.header.back}
            </Text>
          </Stack>
        </TouchableOpacity>
      </AppHeader.Left>
      <AppHeader.Title>
        <Text
          variant={
            isTablet
              ? 'Font Inter/Subtitle/Bold 14px'
              : 'Font Montserrat/Subtitle/Minimize 1 Bold 11px'
          }
          fontWeight={isTablet ? undefined : '800'}
          style={styles.headerTitle}>
          {STRINGS.player.profile.header.title}
        </Text>
      </AppHeader.Title>
      <AppHeader.Right>
        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => {
            // This screen is nested under Drawer -> Tabs -> Stack. MutatePlayer lives in the
            // AuthStack, so we navigate to a parent navigator route.
            (navigation as any).navigate('MutatePlayer', {
              mode: 'edit',
              playerId: profile.id,
              ...(mutateDeleteReturnTo ? { deleteReturnHomeScreen: mutateDeleteReturnTo } : {}),
            });
          }}>
          <Stack direction="row" align="center" gap={scale(2)}>
            <Text
              variant={isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'}
              style={styles.headerActionText}>
              {STRINGS.player.profile.header.edit}
            </Text>
            <SvgIcon name="IcEdit" size={24} color={colors.Blue} />
          </Stack>
        </TouchableOpacity>
      </AppHeader.Right>
    </AppHeader>
  );
}

function PlayerProfileTab({ excludeOverview = false }: { excludeOverview?: boolean }) {
  const styles = useStyles();
  const { activeTab, setActiveTab, tabs } = usePlayerProfileContext();
  const { isTablet } = useScaling();
  const tabList = excludeOverview ? tabs.filter(tab => tab.key !== 'overview') : tabs;

  return (
    <Stack
      direction="row"
      align="center"
      style={styles.tabsRow}
      zIndex={999}
      backgroundColor={!isTablet ? 'white' : 'transparent'}>
      {tabList.map(tab => (
        <TouchableOpacity key={tab.key} onPress={() => setActiveTab(tab.key)}>
          <Text
            variant={
              activeTab === tab.key
                ? isTablet
                  ? 'Font Inter/Subtitle/Bold 12px'
                  : 'Font Inter/Subtitle/Bold 10px'
                : isTablet
                  ? 'Font Inter/Subtitle/Regular 12px'
                  : 'Font Inter/Subtitle/Regular'
            }
            style={activeTab === tab.key ? styles.tabActive : styles.tabInactive}>
            {tab.label}
          </Text>
        </TouchableOpacity>
      ))}
    </Stack>
  );
}

function PlayerProfileBodyContent({ forceTab }: { forceTab?: ProfileTabKey }) {
  const { activeTab, profile, saveNote } = usePlayerProfileContext();
  const visibleTab = forceTab ?? activeTab;
  const normalizedPhone = profile.phone?.startsWith('+') ? profile.phone : `+1${profile.phone}`;
  const formatter = new AsYouType();
  const formattedPhone = formatter.input(`${normalizedPhone}`);

  const phoneNumber = formattedPhone;

  switch (visibleTab) {
    case 'overview':
      return (
        <PlayerProfileOverviewContent
          parentName={profile.parentName}
          phone={profile.phone && profile.phone !== DEFAULT_PROFILE.phone ? phoneNumber : ''}
          email={profile.email}
        />
      );
    case 'progressTracker':
      return <PlayerProfileProgressTrackerContent profile={profile} />;
    case 'notes':
      return <PlayerProfileNotesContent notes={profile.notes} onSaveNote={saveNote} />;
    case 'equipment':
      return (
        <PlayerProfileEquipment
          welcomeKit={profile.welcomeKit ?? []}
          profileId={profile.id}
          clubsneeded={profile.clubsneeded}
          dominantHand={profile.dominantHand}
          height={profile.height ? `${profile.height}` : ''}
        />
      );
    default:
      return <Stack />;
  }
}
export const PlayerProfile = Object.assign(PlayerProfileRoot, {
  Header: PlayerProfileHeader,
  Top: PlayerProfileTop,
  Tab: PlayerProfileTab,
  Content: PlayerProfileBodyContent,
});

const useStyles = createStyles(({ scale }) => ({
  headerTitle: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: colors['6'],
  },
  headerButton: {
    paddingHorizontal: scale(4),
  },
  headerActionText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  avatarWrap: {
    marginTop: scale(-72),
    alignSelf: 'center',
    borderRadius: 999,
    borderWidth: 2,
    borderColor: 'white',
    overflow: 'hidden',
    backgroundColor: 'white',
  },
  profileBlock: {
    paddingTop: scale(12),
    paddingHorizontal: scale(16),
  },
  playerName: {
    color: colors.Blue,
    textTransform: 'uppercase',
    textAlign: 'center',
  },
  metaRow: {
    marginTop: scale(8),
  },
  levelPill: {
    paddingHorizontal: scale(16),
    paddingVertical: scale(2),
    borderRadius: scale(2),
  },
  levelText: {
    color: colors.Blue,
    textTransform: 'uppercase',
  },
  ageText: {
    color: colors['10'],
  },
  tabsRow: {
    width: '100%',
    paddingTop: scale({ phone: 23, tablet: 16 }),
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['6'],
  },
  tabActive: {
    color: colors.Blue,
    borderBottomWidth: 4,
    borderBottomColor: colors.Blue,
    paddingBottom: scale(8),
    marginHorizontal: scale({ phone: 16, tablet: 0 }),
    paddingHorizontal: scale({ phone: 0, tablet: 24 }),
    marginLeft: scale({ phone: 16, tablet: 16 }),
  },
  tabInactive: {
    color: colors['6'],
    paddingBottom: scale(8),
    borderBottomWidth: 4,
    borderBottomColor: 'transparent',
    marginHorizontal: scale({ phone: 16, tablet: 0 }),
    paddingHorizontal: scale({ phone: 0, tablet: 24 }),
    marginLeft: scale({ phone: 16, tablet: 16 }),
  },
  infoLabel: {
    color: colors['10'],
  },
}));
